import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";

const VendorProfileDialog = ({ open, onClose, vendor }) => {
  const [tab, setTab] = useState(0);

  const parseAddress = (address = "") => {
    const [addressLine1, addressLine2, city, state, country, postalCode] = (
      address || ""
    ).split("|");

    return {
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      postalCode,
    };
  };

  const parseBankDetails = (bank = "") => {
    const [accountName, accountNumber, bankName, ifscCode, swiftCode] = (
      bank || ""
    ).split("|");

    return {
      accountName,
      accountNumber,
      bankName,
      ifscCode,
      swiftCode,
    };
  };

  const parseTaxDetails = (tax = "") => {
    const [gst, taxId, cin, pan] = (tax || "").split("|");

    return {
      gst,
      taxId,
      cin,
      pan,
    };
  };

  const address = parseAddress(vendor.address);
  const bank = parseBankDetails(vendor.bank_deatils);
  const tax = parseTaxDetails(vendor.tax_id);

  const readOnlyProps = {
    InputProps: { readOnly: true },
    fullWidth: true,
    size: "small",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Vendor Profile</DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Company & Address" />
        <Tab label="Tax Details" />
        <Tab label="Bank Details" />
        <Tab label="CRM & Books IDs" />
        <Tab label="Commercials" />
      </Tabs>

      <DialogContent sx={{ mt: 2 }}>
        {/* TAB 1 */}
        {tab === 0 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Company Name"
              value={vendor.company}
              {...readOnlyProps}
            />
            <TextField label="Website" value="" {...readOnlyProps} />

            <TextField
              label="Address Line 1"
              value={address.addressLine1}
              {...readOnlyProps}
            />
            <TextField
              label="Address Line 2"
              value={address.addressLine2}
              {...readOnlyProps}
            />

            <TextField label="City" value={address.city} {...readOnlyProps} />
            <TextField label="State" value={address.state} {...readOnlyProps} />

            <TextField
              label="Country"
              value={address.country}
              {...readOnlyProps}
            />
            <TextField
              label="Postal Code"
              value={address.postalCode}
              {...readOnlyProps}
            />
          </Box>
        )}

        {/* TAB 2 */}
        {tab === 1 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField label="GST Number" value={tax.gst} {...readOnlyProps} />
            <TextField label="PAN Number" value={tax.pan} {...readOnlyProps} />
            <TextField label="CIN" value={tax.cin} {...readOnlyProps} />
            <TextField label="Tax ID" value={tax.taxId} {...readOnlyProps} />
          </Box>
        )}

        {/* TAB 3 */}
        {tab === 2 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Account Name"
              value={bank.accountName}
              {...readOnlyProps}
            />
            <TextField
              label="Account Number"
              value={bank.accountNumber}
              {...readOnlyProps}
            />
            <TextField
              label="Bank Name"
              value={bank.bankName}
              {...readOnlyProps}
            />
            <TextField
              label="IFSC Code"
              value={bank.ifscCode}
              {...readOnlyProps}
            />
            <TextField
              label="SWIFT Code"
              value={bank.swiftCode || "-"}
              {...readOnlyProps}
            />
          </Box>
        )}

        {/* TAB 4 */}
        {tab === 3 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="CRM Account ID"
              value={vendor.vendor_crm_acc_id}
              {...readOnlyProps}
            />
            <TextField
              label="CRM Contact ID"
              value={vendor.vendor_crm_contact_id}
              {...readOnlyProps}
            />
            <TextField
              label="Desk Account ID"
              value={vendor.vendor_desk_acc_id || "-"}
              {...readOnlyProps}
            />
            <TextField
              label="Desk Contact ID"
              value={vendor.vendor_desk_contact_id}
              {...readOnlyProps}
            />
            <TextField
              label="Books Vendor ID"
              value={vendor.books_venodr_id || "-"}
              {...readOnlyProps}
            />
          </Box>
        )}

        {/* TAB 5 — carve-out margin. The vendor authors the buyer-facing price;
            this rate decides what they receive. Renegotiating is explicit and
            previewed, never a silent recalculation. */}
        {tab === 4 && <MarginTab vendorId={vendor.id} />}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Carve-out margin for one vendor.
 *
 * Buyer pays `marketplace_price`; the vendor receives that less this margin.
 * The number is stored on the vendor so every payout is re-derivable — before
 * this, both prices were typed by hand and the rate behind them was recorded
 * nowhere.
 *
 * Changing it does NOT rewrite live prices. It changes what future derivations
 * produce, so the impact line reports how many live tiers currently sit at a
 * different effective rate and would move the next time they are saved.
 */
const MarginTab = ({ vendorId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [ratePct, setRatePct] = useState("");
  const [preview, setPreview] = useState(null);

  const base = `${process.env.REACT_APP_SERVER_URL}/admin/vendors/${vendorId}/margin`;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(base, { credentials: "include" });
      const j = await r.json();
      if (!j.success) throw new Error(j.msg || "Could not load margin");
      setData(j);
      setRatePct(
        j.current.margin_rate === null
          ? ""
          : String(Math.round(j.current.margin_rate * 10000) / 100),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    if (vendorId != null) load();
  }, [vendorId, load]);

  // Model the change before committing it, so the admin sees the payout
  // consequence rather than discovering it in a dispute.
  const runPreview = async () => {
    setError("");
    setNotice("");
    const frac = Number(ratePct) / 100;
    if (!Number.isFinite(frac) || frac < 0 || frac >= 1) {
      setError("Enter a percentage between 0 and 100.");
      return;
    }
    try {
      const r = await fetch(`${base}?margin_rate=${frac}`, {
        credentials: "include",
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.msg || "Preview failed");
      setPreview(j.proposed_impact);
    } catch (e) {
      setError(e.message);
    }
  };

  const apply = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const body = ratePct === "" ? {} : { margin_rate: Number(ratePct) / 100 };
      const r = await fetch(base, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.msg || "Could not save");
      setNotice(j.msg);
      setPreview(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={22} />;
  if (error && !data) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const inr = (n) =>
    "\u20b9" +
    Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const cur = data.current;
  const impact = preview || data.current_impact;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity={cur.source === "platform" ? "warning" : "info"}>
        {cur.source === "platform"
          ? "No negotiated rate recorded for this vendor — the platform default applies."
          : "Negotiated rate recorded on this vendor."}{" "}
        {cur.kind === "flat"
          ? `Easexpense keeps ${inr(cur.value)} per unit.`
          : `Easexpense keeps ${(cur.value * 100).toFixed(2)}% of the buyer price.`}
      </Alert>

      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <TextField
          label="Margin %"
          value={ratePct}
          onChange={(e) => {
            setRatePct(e.target.value);
            setPreview(null);
          }}
          helperText="Leave blank to fall back to the platform default"
          size="small"
          fullWidth
        />
        <Box display="flex" alignItems="flex-start" gap={1} pt={0.5}>
          <Button variant="outlined" onClick={runPreview} disabled={saving}>
            Preview impact
          </Button>
          <Button variant="contained" onClick={apply} disabled={saving}>
            {saving ? "Saving…" : "Apply"}
          </Button>
        </Box>
      </Box>

      <Box border="1px solid #e0e0e0" borderRadius={1} p={1.5}>
        <Typography variant="subtitle2">
          {preview ? "If you apply this rate" : "Against the current rate"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {impact.live_tiers} live tier(s) ·{" "}
          <b>{impact.tiers_that_would_move}</b> sit at a different effective
          rate and would move the next time they are saved · payout delta{" "}
          <b>{inr(impact.payout_delta)}</b>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Applying a rate changes nothing live. Prices move only through the
          normal submit → approve path, where you see a before/after diff.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
    </Box>
  );
};

export default VendorProfileDialog;
